import { useContext, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { COMMON_MIXPANEL_PAGE, useFlags, useLyraIntl } from '@lyrahealth-inc/shared-app-logic'
import { useToolkitAvailabilityMap as useToolkitAvailabilityMapShared } from '@lyrahealth-inc/shared-app-logic/src/features/wellnessToolkit/hooks/useToolkitAvailabilityMap'
import { ProductTypeContext } from '@lyrahealth-inc/ui-core-crossplatform'

import {
  getHideChildSearchCTA,
  getHideDependentField,
  getIsCoachingEnabled,
  getIsCoachingForParentsEnabled,
  getIsTherapyEnabled,
  getLibraryDisabled,
} from '../../data/customer/customerSelectors'
import {
  getId,
  getIsUserInternational,
  getIsUserLoggedIn,
  getIsUserTeen,
  getUserCountryName,
  getUserDisplayLanguageOrDefault,
  getUserDOB,
  getUserGenderString,
} from '../../data/user/userSelectors'
import { getLyraTherapyEpisodes } from '../../features/lyraTherapy/data/LyraTherapySelectors'
import { postSpeculativeSearch } from '../../features/triage/data/triageActions'
import { getRiskFactors, getSearches } from '../../features/triage/data/triageSelectors'

export const useToolkitAvailabilityMap = (page?: COMMON_MIXPANEL_PAGE) => {
  const {
    isFamilyHubEnabled,
    isCoachingEntryPointEnabled,
    parentToolKitEntryPointEnabled,
    isSpeculativeFilterEnabled,
    speculativeSearchThresholds,
    isNeurodiversityCOEEnabled,
    isCriticalIncidentSupportToolkitEnabled,
    isHealthChallengesToolkitEnabled,
    isWomensHealthToolkitEnabled,
  } = useFlags()
  const { isLyraSelect } = useContext(ProductTypeContext)

  const isEssentialsEnabled = !useSelector(getLibraryDisabled)
  const isCoachingEnabled = useSelector(getIsCoachingEnabled)
  const isTherapyEnabled = useSelector(getIsTherapyEnabled)
  const isCoachingForParentsEnabled = useSelector(getIsCoachingForParentsEnabled)
  const hideChildSearchCTA = useSelector(getHideChildSearchCTA)
  const hideDependentField = useSelector(getHideDependentField)
  const isUserTeen = useSelector(getIsUserTeen)
  const isInternationalUser = useSelector(getIsUserInternational)
  const triageSearches = useSelector(getSearches)
  const triageSearchesMemoized = useMemo(() => {
    return triageSearches?.toJS() ?? []
  }, [triageSearches])
  const riskFactors = useSelector(getRiskFactors)
  const riskFactorsMemoized = useMemo(() => {
    return riskFactors?.toJS()
  }, [riskFactors])
  const episodes = useSelector(getLyraTherapyEpisodes)
  const episodesMemoized = useMemo(() => {
    return episodes?.toJS() ?? []
  }, [episodes])
  const lyraId = useSelector(getId)
  const isUserLoggedIn = useSelector(getIsUserLoggedIn)
  const userDisplayLanguage = useSelector(getUserDisplayLanguageOrDefault)
  const userDOB = useSelector(getUserDOB)
  const userCountryName = useSelector(getUserCountryName)
  const { activeLanguage } = useLyraIntl()
  const displayLanguage = isUserLoggedIn ? userDisplayLanguage : activeLanguage
  const userGender = useSelector(getUserGenderString)

  return useToolkitAvailabilityMapShared({
    isLyraSelect,
    page,
    lyraId,
    isEssentialsEnabled,
    isCoachingEnabled,
    isTherapyEnabled,
    isCoachingForParentsEnabled,
    hideChildSearchCTA,
    hideDependentField,
    isUserTeen,
    triageSearches: triageSearchesMemoized,
    episodes: episodesMemoized,
    isUserLoggedIn,
    isInternationalUser,
    postSpeculativeSearch,
    riskFactors: riskFactorsMemoized,
    userDOB,
    userCountryName,
    userGender,
    displayLanguage,
    isFamilyHubEnabled,
    isCoachingEntryPointEnabled,
    isCoachingForParentsEntryPointEnabled: parentToolKitEntryPointEnabled,
    isCriticalIncidentSupportToolkitEnabled,
    isNeurodiversityCOEEnabled,
    isHealthChallengesCOEEnabled: isHealthChallengesToolkitEnabled,
    isWomensHealthToolkitEnabled,
    isSpeculativeFilterEnabled,
    speculativeSearchThresholds,
  })
}
